import { usePageTitle } from '../../hooks/usePageTitle';
import GuideSidebar from '../../components/guides/GuideSidebar';
import GuideAccordion from '../../components/guides/GuideAccordion';
import ComparisonTable from '../../components/guides/ComparisonTable';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';

export default function BuyingOwnership() {
  usePageTitle('Buying & Owning a Yacht | Complete Guide');

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
        <div className="lg:col-span-1"><GuideSidebar /></div>
        <div className="lg:col-span-3">
          <h1 className="text-4xl md:text-5xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Buying & Ownership Guide</h1>
          <p className="text-lg text-white/70 mb-8">Everything you need to know about purchasing and owning a yacht in Southeast Asia.</p>

          <div className="space-y-2">
            <GuideAccordion title="The Yacht Buying Process" defaultOpen={true}>
              <ol className="space-y-4">
                <li className="flex gap-4"><span className="text-gold font-bold text-xl">1.</span><div><strong>Define Your Needs:</strong> Size, type, intended use, cruising area, budget (purchase + 10-15% annual operating costs).</div></li>
                <li className="flex gap-4"><span className="text-gold font-bold text-xl">2.</span><div><strong>Hire a Broker:</strong> Southeast Asia specialist brokers understand local markets, regulations, and can negotiate better deals.</div></li>
                <li className="flex gap-4"><span className="text-gold font-bold text-xl">3.</span><div><strong>Inspect & Survey:</strong> Professional marine survey (hull, engine, systems). Cost: $2,000-10,000 depending on size.</div></li>
                <li className="flex gap-4"><span className="text-gold font-bold text-xl">4.</span><div><strong>Sea Trial:</strong> Test yacht under power and sail. Check all systems, maneuverability, engine noise.</div></li>
                <li className="flex gap-4"><span className="text-gold font-bold text-xl">5.</span><div><strong>Negotiate & Purchase:</strong> Offer, counter-offer, contract. Legal review recommended.</div></li>
                <li className="flex gap-4"><span className="text-gold font-bold text-xl">6.</span><div><strong>Registration & Delivery:</strong> Choose flag state, transfer ownership, arrange delivery or sail away.</div></li>
              </ol>
            </GuideAccordion>

            <GuideAccordion title="Cost Breakdown">
              <ComparisonTable
                title="Total Cost of Ownership (Annual %)"
                headers={['Expense Category', '% of Purchase Price', 'Example (60ft/$1M yacht)']} 
                rows={[
                  ['Crew Salaries', '8-12%', '$80,000-120,000'],
                  ['Fuel', '2-5%', '$20,000-50,000'],
                  ['Maintenance', '5-10%', '$50,000-100,000'],
                  ['Insurance', '0.5-1.5%', '$5,000-15,000'],
                  ['Dockage/Moorage', '2-4%', '$20,000-40,000'],
                  ['**Total Annual**', '**18-33%**', '**$175,000-325,000**'],
                ]}
              />
              <div className="bg-white/5 p-4 mt-4 border-l-2 border-gold">
                <p className="text-white/90"><strong>Charter Offset:</strong> Commercial charter can recover 30-60% of operating costs (Thailand/Indonesia markets).</p>
              </div>
            </GuideAccordion>

            <GuideAccordion title="Flag State Registration">
              <p>Where you register your yacht affects taxes, regulations, and resale value.</p>
              <ComparisonTable
                headers={['Flag', 'Pros', 'Cons', 'SE Asia Use']}
                rows={[
                  ['Singapore', 'Tax benefits, reputation, regional', 'Strict regulations', 'Very popular'],
                  ['Marshall Islands', 'Low fees, privacy, easy', 'Less prestige', 'Common for expats'],
                  ['Cayman Islands', 'Tax-free, British standards', 'Higher fees', 'Superyachts'],
                  ['Thailand', 'Local registration for charters', 'Complex for foreigners', 'Thai-owned only'],
                  ['UK (Red Ensign)', 'Prestige, VAT paid access', 'Higher costs', 'Traditional choice'],
                ]}
              />
            </GuideAccordion>

            <GuideAccordion title="Ownership Structures">
              <ul className="space-y-3">
                <li className="flex gap-2"><span className="text-gold">•</span><div><strong>Personal Ownership:</strong> Simplest. Register in your name. Tax implications vary by country.</div></li>
                <li className="flex gap-2"><span className="text-gold">•</span><div><strong>Company Ownership (LLC/Corporation):</strong> Liability protection, easier to sell/transfer, potential tax benefits.</div></li>
                <li className="flex gap-2"><span className="text-gold">•</span><div><strong>Fractional Ownership:</strong> Share yacht with 2-10 co-owners. Reduces costs, limits availability.</div></li>
                <li className="flex gap-2"><span className="text-gold">•</span><div><strong>Charter Management:</strong> Charter company manages yacht, you get usage days + income share.</div></li>
              </ul>
            </GuideAccordion>

            <GuideAccordion title="Financing & Insurance">
              <p><strong>Yacht Financing:</strong></p>
              <ul className="space-y-2 mt-2">
                <li className="flex gap-2"><span className="text-gold">•</span><span>Down payment: 20-30% typical</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Terms: 10-20 years for new yachts, 5-15 for used</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Interest rates: 4-8% (varies by country/creditworthiness)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>International lenders: Coutts, UBS, local Asian banks</span></li>
              </ul>
              <p className="mt-4"><strong>Insurance Requirements:</strong></p>
              <ul className="space-y-2 mt-2">
                <li className="flex gap-2"><span className="text-gold">•</span><span>Hull & Machinery: Covers damage/loss to yacht</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>P&I (Protection & Indemnity): Liability coverage</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Crew Insurance: Workers comp, medical</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Southeast Asia: Additional coverage for typhoons, piracy (rare)</span></li>
              </ul>
            </GuideAccordion>

            <GuideAccordion title="Tax Considerations">
              <p className="text-white/90">Tax rules are complex and vary by country. Always consult a maritime tax advisor.</p>
              <div className="bg-white/5 p-4 mt-4">
                <h4 className="text-gold font-semibold mb-2">Key Tax Issues:</h4>
                <ul className="space-y-2">
                  <li className="flex gap-2"><span className="text-gold">•</span><span><strong>VAT/GST:</strong> EU requires VAT paid, SE Asia varies (Singapore GST, Thailand VAT)</span></li>
                  <li className="flex gap-2"><span className="text-gold">•</span><span><strong>Import Duties:</strong> Temporary import exemptions for cruising (6-12 months typical)</span></li>
                  <li className="flex gap-2"><span className="text-gold">•</span><span><strong>Charter Income Tax:</strong> If chartering, income taxable in operating country</span></li>
                  <li className="flex gap-2"><span className="text-gold">•</span><span><strong>Capital Gains:</strong> On sale, varies by flag state and owner residence</span></li>
                </ul>
              </div>
            </GuideAccordion>
          </div>

          <div className="mt-12 glass-heavy p-10 text-center">
            <h3 className="text-2xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Ready to Start Your Ownership Journey?</h3>
            <p className="text-white/70 mb-6">Browse our curated selection of yachts for sale in Southeast Asia, or contact our team for personalized guidance.</p>
            <Link to="/listings"><Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D]">View Yachts for Sale</Button></Link>
          </div>

          <div className="mt-12 flex gap-4">
            <Link to="/guides/hull-design"><Button variant="outline" className="border-gold text-gold hover:bg-gold hover:text-[#0A192F]">← Hull & Design</Button></Link>
            <Link to="/guides/maintenance-safety"><Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D]">Next: Maintenance & Safety →</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
